/**
 * The HTTP client.
 *
 * Three things it does that the legacy client did not:
 *
 * 1. **Single-flight refresh.** When ten requests get 401 at once, one refresh runs and all ten
 *    wait for it. The old client fired ten refreshes and raced them.
 * 2. **Never navigates.** A dead session raises an event; the shell opens a re-auth dialog in
 *    place and replays the request. It does not throw the user at /login and lose their work.
 *    And it only calls a session dead when the server says so: a refresh that fails for any other
 *    reason (offline, a deploy in progress) leaves the session alone.
 * 3. **Per-call timeouts.** A 45s timeout for a JSON call and for a multi-video zip is the same
 *    number applied to two very different operations; each call site picks its own.
 */

import { ApiError, networkError, parseApiError, timeoutError } from './errors';
import {
  broadcastSession,
  clearLegacyRefreshToken,
  clearTokens,
  getAccessToken,
  getLegacyRefreshToken,
  setTokens,
} from './tokens';
import { getSelectedWorkspaceId } from '@/domains/workspaces/storage';

export const API_BASE = '/api/v1';

const DEFAULT_TIMEOUT_MS = 30_000;
/** Downloads stream whole videos; they get their own, much longer budget. */
export const DOWNLOAD_TIMEOUT_MS = 10 * 60_000;

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  timeoutMs?: number;
  /** Skip the refresh-and-retry dance (used by the auth endpoints themselves). */
  skipAuthRefresh?: boolean;
  query?: Record<string, string | number | boolean | undefined | null>;
}

/* ───── Session-expiry notification ───── */

type SessionExpiredHandler = () => Promise<boolean>;

let sessionExpiredHandler: SessionExpiredHandler | null = null;

/**
 * Register what should happen when the refresh token is also dead.
 * The shell supplies a handler that opens the re-auth dialog and resolves `true` once the
 * user has signed back in, so the interrupted request can be replayed.
 */
export function setSessionExpiredHandler(handler: SessionExpiredHandler | null): void {
  sessionExpiredHandler = handler;
}

/* ───── Single-flight refresh ───── */

/**
 * How a refresh ended. Three outcomes, not two, because "the server said no" and "we could not
 * ask" call for opposite reactions: the first ends the session, the second must not.
 */
export type RefreshOutcome =
  | { status: 'ok'; accessToken: string }
  /** The server looked at the refresh token and refused it: expired, revoked, account disabled. */
  | { status: 'rejected' }
  /** No verdict: offline, timed out, or the server or a proxy failed. The session may be fine. */
  | { status: 'unavailable' };

/** A refresh that hangs would leave every request behind it hanging too. */
const REFRESH_TIMEOUT_MS = 15_000;

async function performRefresh(): Promise<RefreshOutcome> {
  // Normally there is nothing to send: the refresh token is an HttpOnly cookie the browser
  // attaches itself. A token in storage means this browser signed in before that was true; it is
  // presented once, and the response sets the cookie so it never has to be again.
  const legacyToken = getLegacyRefreshToken();

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: legacyToken ? { 'Content-Type': 'application/json' } : undefined,
      body: legacyToken ? JSON.stringify({ refresh_token: legacyToken }) : undefined,
      signal: controller.signal,
    });

    if (res.ok) {
      const data = (await res.json().catch(() => null)) as { access_token?: string } | null;
      // A 200 with no token is a broken proxy, not a verdict on the session.
      if (!data?.access_token) return { status: 'unavailable' };
      setTokens(data.access_token);
      clearLegacyRefreshToken();
      return { status: 'ok', accessToken: data.access_token };
    }

    // Only these two mean the token itself was refused. A 5xx during a deploy, a 429 from a
    // rate limiter or a 502 from a proxy say nothing about it — and treating them as if they did
    // is how a blip used to sign every open tab out for good.
    if (res.status === 401 || res.status === 403) return { status: 'rejected' };
    return { status: 'unavailable' };
  } catch {
    return { status: 'unavailable' };
  } finally {
    window.clearTimeout(timer);
  }
}

let refreshInFlight: Promise<RefreshOutcome> | null = null;

/** All concurrent 401s share one refresh promise. */
function refreshOnce(): Promise<RefreshOutcome> {
  if (!refreshInFlight) {
    refreshInFlight = performRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Resume a session from its cookie. Shared with every request that hits a 401. */
export const refreshSession = refreshOnce;

/**
 * The session is over: forget it, tell the other tabs, and give the shell the chance to sign the
 * user back in on the spot. Resolves `true` if it did.
 */
async function endSession(): Promise<boolean> {
  clearTokens();
  broadcastSession({ type: 'logout' });
  return sessionExpiredHandler ? sessionExpiredHandler() : false;
}

/** Milliseconds of life left in a JWT access token, or null when it cannot be read. */
function accessTokenTtlMs(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp * 1000 - Date.now() : null;
  } catch {
    return null;
  }
}

/** Refresh when this little is left, so a request is not sent with a token that dies in flight. */
const EXPIRY_MARGIN_MS = 60_000;

/**
 * The access token to send with a request that cannot simply be replayed.
 *
 * The usual path is reactive: send, get a 401, refresh, send again. That is no good for an upload
 * that streams a large body, or for a route that has no retry of its own, so those look at the
 * token's own expiry first and refresh ahead of time. An unreadable token is left to the reactive
 * path rather than second-guessed.
 */
export async function ensureFreshAccessToken(): Promise<string | null> {
  const token = getAccessToken();
  const ttl = token ? accessTokenTtlMs(token) : null;
  if (!token || ttl === null || ttl > EXPIRY_MARGIN_MS) return token;

  const outcome = await refreshOnce();
  if (outcome.status === 'ok') return outcome.accessToken;
  // Rejected: the session really is over, so say so in the usual way. Unavailable: send what we
  // have and let the request find out.
  if (outcome.status === 'rejected') await endSession();
  return getAccessToken();
}

/* ───── Core fetch ───── */

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

function buildInit(options: RequestOptions, token: string | null, signal: AbortSignal): RequestInit {
  const headers = new Headers(options.headers);
  const isFormData = options.body instanceof FormData;

  if (options.body !== undefined && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);
  // The browser never borrows a paired PC for OCR/ASR. The Electron shell overwrites this
  // header to `desktop` at the session boundary, where remote page JavaScript cannot spoof the
  // shell accidentally. Older clients that send no header are also treated as web by the API.
  headers.set('X-VideoGrab-Client', 'web');
  const workspaceId = getSelectedWorkspaceId();
  if (workspaceId && !headers.has('X-Workspace-ID')) headers.set('X-Workspace-ID', workspaceId);

  return {
    ...options,
    headers,
    signal,
    body:
      options.body === undefined
        ? undefined
        : isFormData
          ? (options.body as FormData)
          : JSON.stringify(options.body),
  };
}

async function readBody(res: Response): Promise<unknown> {
  const contentType = res.headers.get('content-type') ?? '';
  if (res.status === 204) return null;
  if (contentType.includes('application/json')) {
    return res.json().catch(() => null);
  }
  const text = await res.text().catch(() => '');
  return text ? { message: text } : null;
}

async function rawFetch(path: string, options: RequestOptions, token: string | null): Promise<Response> {
  const controller = new AbortController();
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);

  // Let a caller-supplied signal (upload cancellation) abort us too.
  if (options.signal) {
    if (options.signal.aborted) controller.abort();
    else options.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    return await fetch(buildUrl(path, options.query), buildInit(options, token, controller.signal));
  } catch (cause) {
    if (controller.signal.aborted) throw timeoutError();
    throw networkError(cause);
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * Perform a request, refreshing and replaying once on 401.
 *
 * `raw` returns the Response itself so blob/header-reading callers can work with it.
 */
export async function requestRaw(path: string, options: RequestOptions = {}): Promise<Response> {
  let res = await rawFetch(path, options, getAccessToken());

  if (res.status === 401 && !options.skipAuthRefresh) {
    const outcome = await refreshOnce();
    if (outcome.status === 'ok') {
      res = await rawFetch(path, options, outcome.accessToken);
    } else if (outcome.status === 'unavailable') {
      // We could not find out whether the session is alive. That is a connectivity problem, not a
      // sign-out: keep the tokens, and report it as one so callers do not treat it as an auth
      // failure and clear the session themselves.
      throw networkError();
    } else {
      // The refresh token is dead too. Ask the shell to re-authenticate in place; if the
      // user signs back in, replay the original request so nothing is lost.
      const recovered = await endSession();
      if (recovered) return rawFetch(path, options, getAccessToken());
    }
  }

  return res;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const res = await requestRaw(path, options);
  const body = await readBody(res);
  if (!res.ok) throw parseApiError(res.status, body);
  return body as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

/* ───── Binary responses ───── */

export interface BlobResult {
  blob: Blob;
  filename: string;
  headers: Headers;
}

/** Pull a filename out of Content-Disposition, preferring the RFC 5987 (UTF-8) form. */
export function parseFilename(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return utf8[1];
    }
  }
  const quoted = disposition.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];
  const plain = disposition.match(/filename=([^;]+)/i);
  return plain?.[1]?.trim() ?? fallback;
}

export async function requestBlob(
  path: string,
  options: RequestOptions = {},
  fallbackName = 'download',
): Promise<BlobResult> {
  const res = await requestRaw(path, { timeoutMs: DOWNLOAD_TIMEOUT_MS, ...options });
  if (!res.ok) {
    const body = await readBody(res);
    throw parseApiError(res.status, body);
  }
  return {
    blob: await res.blob(),
    filename: parseFilename(res.headers.get('content-disposition'), fallbackName),
    headers: res.headers,
  };
}

/** Save a blob to disk from the browser. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick; revoking synchronously cancels the download in Safari.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ───── Uploads with progress ───── */

export interface UploadOptions {
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

/**
 * XHR rather than fetch: fetch still has no upload-progress event, and a 500MB video upload
 * with no progress bar is not an acceptable experience.
 */
export async function uploadWithProgress<T>(
  path: string,
  formData: FormData,
  { onProgress, signal }: UploadOptions = {},
): Promise<T> {
  // An upload cannot be replayed on a 401 the way a small request can, so make sure the token
  // outlives it before starting. Someone who picks a file after half an hour on the page would
  // otherwise upload it, gigabytes and all, only to be told their session had ended.
  const token = await ensureFreshAccessToken();

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}${path}`);

    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.setRequestHeader('X-VideoGrab-Client', 'web');

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });

    xhr.addEventListener('load', () => {
      let body: unknown = null;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        body = xhr.responseText ? { message: xhr.responseText } : null;
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as T);
      else reject(parseApiError(xhr.status, body));
    });

    xhr.addEventListener('error', () => reject(networkError()));
    xhr.addEventListener('timeout', () => reject(timeoutError()));
    xhr.addEventListener('abort', () =>
      reject(new ApiError({ status: 0, code: 'NETWORK_ERROR', message: 'Đã huỷ tải lên' })),
    );

    // The refresh above is an await, so the caller may have cancelled before we got here.
    if (signal?.aborted) {
      reject(new ApiError({ status: 0, code: 'NETWORK_ERROR', message: 'Đã huỷ tải lên' }));
      return;
    }
    signal?.addEventListener('abort', () => xhr.abort(), { once: true });

    xhr.send(formData);
  });
}
